import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Box, Typography,
  Card, CardContent, Chip,
  useTheme, Paper, Stack,
  Fade, Zoom, alpha
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import DevicesIcon from '@mui/icons-material/Devices';
import CodeIcon from '@mui/icons-material/Code';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import GroupsIcon from '@mui/icons-material/Groups';
import TimelineIcon from '@mui/icons-material/Timeline';
import BrushIcon from '@mui/icons-material/Brush';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import LanguageIcon from '@mui/icons-material/Language';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

// Professional color palette (Same as other pages)
const colors = {
  primary: '#4551be', // Exact blue
  secondary: '#6b77d4', // Lighter blue accent
  success: '#10b981', // Green
  warning: '#f59e0b', // Orange
  dark: '#1e293b', // Dark text
  light: '#f0f4f8', // Light background
  gray: '#64748b', // Slate 500
  accent: '#4551be',
  textSecondary: '#64748b', // Dark grey for secondary text
  gradient: 'linear-gradient(135deg, #4551be 0%, #6b77d4 100%)'
};

// Styled components
const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px -12px ${alpha(colors.primary, 0.2)}`
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5)
}));

const AppDevelopment = () => {
  const theme = useTheme();
  const [activeService, setActiveService] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress((scrolled / totalHeight) * 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Services Data
  const services = [
    {
      id: 1,
      title: 'Web Application Development',
      description: 'Custom web applications built with modern frameworks like React, Next.js and Node.js for scalable, high-performance solutions.',
      icon: <CodeIcon />,
      color: colors.primary,
      features: ['React/Next.js', 'Node.js', 'MongoDB/PostgreSQL', 'REST/GraphQL APIs'],
    },
    {
      id: 2,
      title: 'Mobile App Development',
      description: 'Native iOS & Android apps and cross-platform solutions using React Native & Flutter with seamless user experiences.',
      icon: <DevicesIcon />,
      color: colors.secondary,
      features: ['iOS/Android', 'React Native', 'Flutter', 'App Store/Play Store'],
    },
    {
      id: 3,
      title: 'UI/UX Design',
      description: 'Beautiful, intuitive interfaces designed with user-centered principles to maximize engagement and conversion.',
      icon: <DesignServicesIcon />,
      color: colors.success,
      features: ['User Research', 'Wireframing', 'Interactive Prototypes', 'Design Systems'],
    },
    {
      id: 4,
      title: 'E-commerce Solutions',
      description: 'Custom e-commerce platforms with integrated payment gateways, inventory management and seamless checkout experiences.',
      icon: <ShoppingCartIcon />,
      color: colors.warning,
      features: ['Payment Integration', 'Product Management', 'Shopping Cart', 'Order Tracking'],
    },
    {
      id: 5,
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure on AWS, Google Cloud and Azure with containerization and microservices architecture.',
      icon: <CloudIcon />,
      color: colors.primary,
      features: ['AWS/Azure/GCP', 'Docker/Kubernetes', 'Serverless', 'DevOps'],
    },
    {
      id: 6,
      title: 'Maintenance & Support',
      description: 'Ongoing maintenance, security updates, performance monitoring and 24/7 technical support for your applications.',
      icon: <SecurityIcon />,
      color: colors.gray,
      features: ['24/7 Support', 'Security Updates', 'Performance Monitoring', 'Backup & Recovery'],
    }
  ];

  // Why Choose Us
  const whyChooseUs = [
    {
      icon: <VerifiedIcon />,
      title: "Proven Expertise",
      points: ["10+ years in app development", "500+ successful projects", "Enterprise-level partnerships"]
    },
    {
      icon: <GroupsIcon />,
      title: "Professional Team",
      points: ["Senior full-stack developers", "UI/UX design experts", "Certified project managers"]
    },
    {
      icon: <RocketLaunchIcon />,
      title: "Results-Driven",
      points: ["300% average ROI for clients", "98% client retention rate", "On-time delivery guarantee"]
    },
    {
      icon: <SupportAgentIcon />,
      title: "Dedicated Support",
      points: ["24/7 technical assistance", "Regular maintenance", "Strategic consulting"]
    },
    {
      icon: <BrushIcon />,
      title: "Quality First",
      points: ["100% code review process", "Automated testing", "Security audits"]
    },
    {
      icon: <AnalyticsIcon />,
      title: "Data-Driven",
      points: ["Analytics integration", "Performance monitoring", "User behavior tracking"]
    }
  ];

  // Process Steps
  const processSteps = [
    {
      step: '01',
      title: 'Discovery',
      description: 'We analyze your requirements, market research and create a detailed project roadmap.',
      icon: '🔍'
    },
    {
      step: '02',
      title: 'Design',
      description: 'Create wireframes, interactive prototypes and design mockups for your approval.',
      icon: '🎨'
    },
    {
      step: '03',
      title: 'Development',
      description: 'Agile development with regular updates, code reviews and sprint demos.',
      icon: '💻'
    },
    {
      step: '04',
      title: 'Testing',
      description: 'Comprehensive testing including QA, performance testing and user acceptance testing.',
      icon: '🧪'
    },
    {
      step: '05',
      title: 'Launch',
      description: 'Deployment to production with monitoring setup and performance optimization.',
      icon: '🚀'
    },
    {
      step: '06',
      title: 'Support',
      description: 'Ongoing maintenance, updates and 24/7 technical support.',
      icon: '🛠️'
    }
  ];

  const technologies = [
    { name: "React Native", icon: "📱", color: "#61DAFB" },
    { name: "Flutter", icon: "🦋", color: "#02569B" },
    { name: "Swift", icon: "🍎", color: "#FA7343" },
    { name: "Kotlin", icon: "🤖", color: "#7F52FF" },
    { name: "Java", icon: "☕", color: "#ED8B00" },
    { name: "Firebase", icon: "🔥", color: "#FFCA28" },
    { name: "Node.js", icon: "🟢", color: "#339933" },
    { name: "MongoDB", icon: "🍃", color: "#47A248" },
    { name: "AWS", icon: "☁️", color: "#FF9900" },
    { name: "Docker", icon: "🐳", color: "#2496ED" }
  ];

  return (
    <Box sx={{ bgcolor: colors.light }}>
      <Navbar />

      {/* Hero Section */}
            <Box
  sx={{
    minHeight: { xs: 'auto', md: '60vh' },
    display: 'flex',
    alignItems: 'center',

    // Navbar overlap fix
   pt: { xs: 9, sm: 7, md: 8 },
    pb: { xs: 7, sm: 9, md: 12 },

    background: `linear-gradient(
      135deg,
      ${alpha(colors.primary, 0.03)} 0%,
      ${alpha(colors.secondary, 0.03)} 100%
    )`,

    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  }}
>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    icon={<RocketLaunchIcon />}
                    label="⚡ APP DEVELOPMENT AGENCY"
                    sx={{
                      mb: 3,
                      mt: { xs: 2, md: 10 },
                      bgcolor: alpha(colors.primary, 0.1),
                      color: colors.primary,
                      fontWeight: 600,
                      border: `1px solid ${alpha(colors.primary, 0.2)}`
                    }}
                  />

                  <Typography variant="h1" sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 3,
                    color: colors.dark
                  }}>
                    Build
                    <Box component="span" sx={{
                      display: 'block',
                      color: colors.primary // Blue text
                    }}>
                      Digital Experiences
                    </Box>
                    That Drive Growth
                  </Typography>

                  <Typography sx={{
                    color: colors.textSecondary,
                    mb: 4,
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    maxWidth: '90%'
                  }}>
                    We create custom web and mobile applications that help businesses scale,
                    engage users and achieve their digital goals with cutting-edge technology.
                  </Typography>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box sx={{
                  animation: `${float} 6s ease-in-out infinite`,
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: '280px', md: '380px' },
                    height: { xs: '280px', md: '380px' },
                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                    background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, ${alpha(colors.secondary, 0.1)} 100%)`,
                    margin: '0 auto',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -15,
                      left: -15,
                      right: -15,
                      bottom: -15,
                      borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
                      background: `linear-gradient(135deg, ${alpha(colors.primary, 0.2)} 0%, ${alpha(colors.secondary, 0.2)} 100%)`,
                      zIndex: -1,
                      animation: `${float} 8s ease-in-out infinite reverse`
                    }
                  }}>
                    <LanguageIcon sx={{
                      fontSize: { xs: 100, md: 150 },
                      color: colors.primary
                    }} />
                  </Box>

                  {/* Floating Elements */}
                  <Box sx={{
                    position: 'absolute',
                    top: '15%',
                    right: '10%',
                    width: 60,
                    height: 60,
                    borderRadius: '15px',
                    background: alpha(colors.success, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 7s ease-in-out infinite`,
                    animationDelay: '1s'
                  }}>
                    <CodeIcon sx={{ color: colors.success }} />
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    bottom: '20%',
                    left: '5%',
                    width: 50,
                    height: 50,
                    borderRadius: '10px',
                    background: alpha(colors.warning, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 8s ease-in-out infinite`,
                    animationDelay: '2s'
                  }}>
                    <DesignServicesIcon sx={{ color: colors.warning }} />
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    top: '30%',
                    left: '15%',
                    width: 45,
                    height: 45,
                    borderRadius: '50%',
                    background: alpha(colors.secondary, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 9s ease-in-out infinite`,
                    animationDelay: '1.5s'
                  }}>
                    <StorageIcon sx={{ color: colors.secondary, fontSize: 25 }} />
                  </Box>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="WHY CHOOSE US"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            We Deliver Excellence
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
            Here's why industry leaders trust us with their most important projects
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {whyChooseUs.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{
                p: 3,
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                }
              }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: colors.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  color: 'white'
                }}>
                  {item.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: colors.dark }}>
                  {item.title}
                </Typography>
                <Stack spacing={1}>
                  {item.points.map((point, idx) => (
                    <FeatureItem key={idx}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: colors.success }} />
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        {point}
                      </Typography>
                    </FeatureItem>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="OUR SERVICES"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              What We Offer
            </Typography>
            <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
              Comprehensive development services tailored to your business needs
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} lg={4} key={service.id}>
                <ServiceCard
                  onMouseEnter={() => setActiveService(service.id)}
                  onMouseLeave={() => setActiveService(0)}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      background: alpha(service.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: service.color
                    }}>
                      {service.icon}
                    </Box>

                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: colors.dark }}>
                      {service.title}
                    </Typography>

                    <Typography paragraph sx={{ color: colors.textSecondary }}>
                      {service.description}
                    </Typography>

                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {service.features.map((feature, idx) => (
                        <FeatureItem key={idx}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: colors.success }} />
                          <Typography variant="body2" sx={{ color: colors.dark }}>{feature}</Typography>
                        </FeatureItem>
                      ))}
                    </Stack>
                  </CardContent>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="OUR PROCESS"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            How We Build Your App
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
            A structured approach to deliver high-quality results on time
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {processSteps.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <Paper sx={{
                p: 3,
                height: '100%',
                position: 'relative',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                }
              }}>
                <Typography variant="h2" sx={{
                  position: 'absolute',
                  top: 10,
                  right: 20,
                  fontSize: '4rem',
                  fontWeight: 900,
                  color: alpha(colors.primary, 0.1),
                  lineHeight: 1
                }}>
                  {item.step}
                </Typography>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{ fontSize: '2.5rem', mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: colors.primary }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    {item.description}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Technology Stack */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="TECHNOLOGY STACK"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              Modern Tools We Use
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2
          }}>
            {technologies.map((tech, index) => (
              <Chip
                key={index}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{tech.icon}</span>
                    <span>{tech.name}</span>
                  </Box>
                }
                sx={{
                  px: 2,
                  py: 3,
                  fontSize: '1rem',
                  bgcolor: 'white',
                  boxShadow: 1,
                  border: `1px solid ${alpha(tech.color, 0.3)}`,
                  color: colors.dark, // Dark text
                  '&:hover': {
                    bgcolor: alpha(tech.color, 0.1),
                    cursor: 'pointer'
                  }
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{
        background: colors.gradient,
        py: 8,
        mt: 4
      }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: '#ffffff' }}>
            Ready to Build Your App?
          </Typography>

          <Typography variant="h6" sx={{ color: alpha('#ffffff', 0.9), mb: 4 }}>
            Join 500+ businesses that have transformed their digital presence with us.
          </Typography>

          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 2, sm: 4 },
            mt: 4,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon sx={{ color: "#ffffff" }} />
              <Typography sx={{ color: "#ffffff", fontSize: { xs: "0.9rem", md: "1rem" } }}>
                lidertechnologysolutions@gmail.com
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PhoneIcon sx={{ color: "#ffffff" }} />
              <Typography sx={{ color: "#ffffff", fontSize: { xs: "0.9rem", md: "1rem" } }}>
                +91 9840369993
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default AppDevelopment;